import { useEffect } from "react"
import type { RedirectLoginOptions } from "@auth0/auth0-react"
import { useAuth0 } from "@auth0/auth0-react"
import { makeVar } from "@apollo/client"

import { usePersistedPortalEntities } from "~utils/hooks/use-persisted-portal-entities"
import {
    getAccessTokenFromBridge,
    isInAppMode,
    subscribeToNativeAuthToken,
} from "~utils/native-auth-bridge"
import {
    captureAuthError,
    getSentryClient,
    SENTRY_AUTH_SOURCES,
    SENTRY_METRICS,
    SPAN_STATUS,
} from "~utils/sentry"

// Storing the getAccessTokenSilently function in this var so that we don't have to
// rely on the hook. Using useAuth in ApolloProvider will cause the entire app to
// re-render and essentially reset all state, losing anything you were doing.
// So, instead, we can import this reactive var to get the function.
export const getAccessTokenFuncVar = makeVar<(() => Promise<string>) | null>(
    null
)
export const accessTokenVar = makeVar<string | null>(null)

// Resolve the bearer token lazily for REST callers (e.g. AI chat, private
// file uploads). Prefer the Auth0 getter so an expired JWT can be refreshed
// transparently; fall back to the cached `accessTokenVar` if the getter has
// not been registered yet (e.g. a call fires before `useAuth` mounted).
export async function getAuthBearerToken(): Promise<string> {
    const getAccessToken = getAccessTokenFuncVar()
    return (await getAccessToken?.()) ?? accessTokenVar() ?? ""
}

// In-app webviews must never start an Auth0 redirect — the native app
// owns authentication and pushes tokens over the bridge.
const loginWithRedirectInAppNoop: (
    options?: RedirectLoginOptions
) => Promise<void> = async () => {}

export function useAuth() {
    const {
        isAuthenticated,
        isLoading,
        getAccessTokenSilently,
        loginWithRedirect,
        logout,
        user: authUser,
        error,
    } = useAuth0()
    const isInApp = isInAppMode()

    useEffect(() => {
        // Auth0Provider's mount-time checkSession is expected to fail inside
        // the native webview, so don't report it in in-app mode.
        if (!error || isInApp) return

        captureAuthError(error, SENTRY_AUTH_SOURCES.AUTH0, error.message)
    }, [error, isInApp])

    // Store the getAccessToken function in the reactive var. This function
    // will be called by apollo when setting context to ensure auth0 can
    // handle token refreshes. Storing token in var as well so that other
    // parts of the app can access it for rest endpoints. Rewire when the
    // in-app flag flips mid-session (e.g. `?in_app=false` reset) so Apollo
    // and the rest of the hook never disagree on the auth source.
    useEffect(() => {
        if (!isInApp) {
            getAccessTokenFuncVar(() =>
                instrumentedGetAccessToken(getAccessTokenSilently)
            )
            return undefined
        }

        getAccessTokenFuncVar(getAccessTokenFromBridge)
        // Keep accessTokenVar fresh from native pushes — it is consumed
        // by REST callers (e.g. AI chat) as a fallback.
        return subscribeToNativeAuthToken(accessTokenVar)
    }, [isInApp])

    const { clearAllPersistedPortalEntities } = usePersistedPortalEntities()

    const wrappedLogout = async () => {
        // The native app owns the session in in-app mode.
        if (isInApp) return

        await logout({
            logoutParams: {
                returnTo: process.env.GATSBY_MB_ROOT_URL,
            },
        })
        clearAllPersistedPortalEntities()
    }

    if (isInApp) {
        return {
            authUser,
            isLoading: false,
            error: undefined,
            isAuthenticated: true,
            getAccessTokenSilently: getAccessTokenFromBridge,
            loginWithRedirect: loginWithRedirectInAppNoop,
            logout: wrappedLogout,
        }
    }

    return {
        authUser,
        isLoading,
        error,
        isAuthenticated,
        getAccessTokenSilently,
        loginWithRedirect,
        logout: wrappedLogout,
    }
}

async function instrumentedGetAccessToken(
    getAccessTokenSilently: () => Promise<string>
): Promise<string> {
    const sentry = getSentryClient()
    const start = Date.now()
    const span = sentry.startInactiveSpan({
        name: "auth0.getAccessTokenSilently",
        op: "auth",
    })

    try {
        const accessToken = await getAccessTokenSilently()
        accessTokenVar(accessToken)
        emitTokenMetrics(start, "success")
        span.setStatus({ code: SPAN_STATUS.OK })
        span.end()
        return accessToken
    } catch (tokenError) {
        emitTokenMetrics(start, "error")
        captureAuthError(
            tokenError,
            SENTRY_AUTH_SOURCES.TOKEN_REFRESH,
            tokenError instanceof Error ? tokenError.message : undefined
        )
        span.setStatus({ code: SPAN_STATUS.ERROR })
        span.end()
        throw tokenError
    }
}

function emitTokenMetrics(start: number, outcome: string): void {
    const sentry = getSentryClient()
    const durationMs = Date.now() - start
    sentry.metrics.distribution(
        SENTRY_METRICS.AUTH_TOKEN_REFRESH_DURATION,
        durationMs,
        {
            unit: "millisecond",
            attributes: { outcome },
        }
    )
}
